/**
 * **HUD の金属の装飾**（Issue #549。手本はギルティギア Xrd の対戦画面）。**差し替えられるようにしてある**。
 *
 * 装飾 1 つ = 1 項目。項目は次を持つ:
 *
 * | 口 | 中身 |
 * |---|---|
 * | `label` | 何の装飾か |
 * | `size` | **描く箱の大きさ（px。舞台 1920×1080 のとき）**。画像を作るときはこの縦横比で作る |
 * | `image` | **差し替えの画像**（透過 PNG / WebP / SVG）。`null` の間は下の `Svg`（簡略版）を描く |
 * | `prompt` | **その画像を別の AI に生成してもらうときのプロンプト**（描きたかったイメージ） |
 * | `Svg` | 簡略版（SVG で描ける範囲の仮の絵） |
 *
 * ## 差し替え方
 *
 * 1. `prompt` を画像生成の AI に渡し、`size` の縦横比の画像を**真緑の背景**で作ってもらい、`scripts/hud-ornament-key.py` で抜く
 *    （テーマの画風・決まりは `../THEME.md`）
 * 2. 画像をこのフォルダ（`themes/climax/hud/`）に置く（例: `emblem.png`）
 * 3. その項目の `image` を `{ src: 読み込んだ画像, aspect: 幅 / 高さ, window: 中の透明な窓の多角形 }` にする
 *    （`aspect` と `window` は画像から測る。測り方は `scripts/hud-ornament-key.py`）
 *
 * 画像は箱いっぱいに引き伸ばして貼る（縦横比を合わせて作ること）。**P2 の側は左右反転して使う**ので、
 * 左右のあるもの（ゲージの枠・顔の枠）は **P1 の向き（左側に置く形）で 1 枚**だけ作ればよい。
 * 中身（LP のゲージの色・数字・顔の絵）は HUD が上に描くので、**装飾は枠だけ**（中は透明）にする。
 */

/*
 * **Gemini（Nano Banana Pro）で生成した画像**（2026-09-25。Issue #549）。真緑の背景で生成して色を抜き、外接で切った透過の PNG。
 * 各項目の `aspect` と `window` は画像から測った値。
 */
import barFrameImage from './barFrame.png';
import emblemImage from './emblem.png';
import portraitFrameImage from './portraitFrame.png';

/** 共通の画風（プロンプトの頭に付ける）。**一次情報は `../THEME.md` の「プロンプト」**（変えるときは両方） */
export const STYLE_PROMPT = [
  'Fighting game HUD ornament in the style of Guilty Gear Xrd (Arc System Works).',
  'Dark gunmetal steel with beveled edges, sharp gothic / mechanical details, thin gold and crimson accent lines,',
  'subtle rim highlights, hard-edged cel-shaded anime look, no text, no characters.',
  'No glow, no haze, no soft light bloom — hard shapes only.',
  'Solid pure green #00FF00 background. Front view, flat 2D UI element, crisp edges, high resolution.',
].join(' ');

/* ---------------- 簡略版の SVG（仮の絵） ---------------- */

/** 金属の塗り（上が明るく下が暗い鋼） */
const Steel = ({ id }) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stopColor="#6d7380" />
    <stop offset="0.45" stopColor="#23262e" />
    <stop offset="0.55" stopColor="#15171c" />
    <stop offset="1" stopColor="#3a3e48" />
  </linearGradient>
);

/** LP のゲージの枠（P1 の向き。左が外側の端＝顔の側、右が中央側で尖る） */
function BarFrameSvg() {
  return (
    <svg viewBox="0 0 700 70" preserveAspectRatio="none" width="100%" height="100%" aria-hidden="true">
      <defs><Steel id="cx-steel-bar" /></defs>
      {/* 外枠（中の窓は透明＝ゲージが見える） */}
      <path
        fillRule="evenodd"
        fill="url(#cx-steel-bar)"
        stroke="#050506"
        strokeWidth="2"
        d="M0 6 L660 6 L698 35 L660 64 L0 64 Z M14 17 L652 17 L676 35 L652 53 L14 53 Z"
      />
      <path d="M14 17 L652 17 L676 35" fill="none" stroke="#c9a14a" strokeWidth="1.6" />
      <path d="M0 6 L660 6" fill="none" stroke="#b01f2a" strokeWidth="2.5" />
      {[80, 200, 320, 440, 560].map((x) => <rect key={x} x={x} y="1" width="10" height="7" fill="#2a2d35" stroke="#050506" strokeWidth="1" />)}
    </svg>
  );
}

/** 中央の紋章（ターン数の枠。歯車の輪と左右の翼の板） */
function EmblemSvg() {
  const teeth = Array.from({ length: 24 }, (_, i) => i * 15);
  return (
    <svg viewBox="0 0 320 180" width="100%" height="100%" aria-hidden="true">
      <defs>
        <Steel id="cx-steel-emb" />
        <radialGradient id="cx-emb-core" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#2a0508" />
          <stop offset="1" stopColor="#07080b" />
        </radialGradient>
      </defs>
      {/* 翼の板（左右のゲージへ繋がる） */}
      <path d="M0 20 L110 20 L130 60 L110 100 L20 100 Z" fill="url(#cx-steel-emb)" stroke="#050506" strokeWidth="2" />
      <path d="M320 20 L210 20 L190 60 L210 100 L300 100 Z" fill="url(#cx-steel-emb)" stroke="#050506" strokeWidth="2" />
      <path d="M8 26 L106 26" stroke="#b01f2a" strokeWidth="3" />
      <path d="M312 26 L214 26" stroke="#b01f2a" strokeWidth="3" />
      {/* 歯車 */}
      <g transform="translate(160 78)">
        {teeth.map((a) => <rect key={a} x="-7" y="-78" width="14" height="16" fill="#2a2d35" stroke="#050506" strokeWidth="1.5" transform={`rotate(${a})`} />)}
        <circle r="66" fill="url(#cx-steel-emb)" stroke="#050506" strokeWidth="3" />
        <circle r="56" fill="url(#cx-emb-core)" stroke="#c9a14a" strokeWidth="2" />
        <circle r="48" fill="none" stroke="#b01f2a" strokeWidth="1.5" strokeDasharray="6 5" />
      </g>
      {/* 下の飾り */}
      <path d="M120 150 L160 176 L200 150" fill="none" stroke="#c9a14a" strokeWidth="2.5" />
    </svg>
  );
}

/** 顔の枠（P1 の向き。斜めに切った額縁） */
function PortraitFrameSvg() {
  return (
    <svg viewBox="0 0 200 160" preserveAspectRatio="none" width="100%" height="100%" aria-hidden="true">
      <defs><Steel id="cx-steel-por" /></defs>
      <path
        fillRule="evenodd"
        fill="url(#cx-steel-por)"
        stroke="#050506"
        strokeWidth="2"
        d="M0 0 L200 0 L176 160 L0 160 Z M10 10 L186 10 L166 150 L10 150 Z"
      />
      <path d="M10 10 L186 10" stroke="#c9a14a" strokeWidth="2" />
      <path d="M0 160 L176 160" stroke="#b01f2a" strokeWidth="4" />
      <path d="M0 0 L40 0 L0 40 Z" fill="#b01f2a" />
    </svg>
  );
}

/* ---------------- 装飾の一覧（ここを差し替える） ---------------- */

/*
 * `window` は中の透明な窓の形（箱に対する割合の多角形。P1 の向き）。HUD はこの形で中身を切り抜く。
 */
export const ORNAMENTS = Object.freeze({
  barFrame: {
    label: 'LP のゲージの枠',
    size: { w: 700, h: 113 },
    /** 差し替えの画像と、その縦横比・窓（`null` なら下の簡略版） */
    image: { src: barFrameImage, aspect: 6.2222, window: [[0.125, 0.364], [0.897, 0.364], [0.926, 0.5], [0.898, 0.631], [0.125, 0.631]] },
    prompt: `${STYLE_PROMPT} A single long thin horizontal HEALTH BAR FRAME spanning almost the full image width, centered vertically, `
      + 'about 1/6 as tall as it is wide. The LEFT end is a square steel clamp block with a small gear (it touches a character portrait). '
      + 'The RIGHT end tapers into a sharp arrow point toward the screen center. Thick dark steel border around a hollow empty window '
      + '(the health fill will be drawn there). Small rivets and notches along the top edge, a thin crimson line on the top edge, a thin gold inner bevel line.',
    Svg: BarFrameSvg,
    svg: { aspect: 10, window: [[0.02, 0.24], [0.931, 0.24], [0.966, 0.5], [0.931, 0.76], [0.02, 0.76]] },
  },
  emblem: {
    label: '中央の紋章（ターン数の枠）',
    size: { w: 520, h: 157 },
    image: { src: emblemImage, aspect: 3.3133, window: null, number: { x: 0.5, y: 0.5 } },
    prompt: `${STYLE_PROMPT} The ornate CENTER EMBLEM that sits between two health bars at the top of the screen: `
      + 'a mechanical gear / clockwork ring in the center with a dark empty round window (a glowing red number will be drawn inside later), '
      + 'flanked by two angular steel wing plates pointing left and right that connect to the health bars. '
      + 'Gothic spikes, gold filigree accents, crimson trim. Perfectly symmetric left-right.',
    Svg: EmblemSvg,
    svg: { aspect: 16 / 9, window: null, number: { x: 0.5, y: 0.43 } },
  },
  portraitFrame: {
    label: '顔の枠',
    size: { w: 200, h: 155 },
    image: { src: portraitFrameImage, aspect: 1.292, window: [[0.198, 0.15], [0.86, 0.15], [0.58, 0.858], [0.106, 0.858], [0.106, 0.3]] },
    prompt: `${STYLE_PROMPT} A CHARACTER PORTRAIT FRAME for the top-left corner of the screen. Slanted shape: the right edge leans inward toward the bottom. `
      + 'Thick dark steel border with a large hollow empty window in the middle (the portrait image will be drawn behind it). '
      + 'A crimson triangle accent in the top-left corner, a gold inner bevel line, small gothic spikes on the outer edge.',
    Svg: PortraitFrameSvg,
    svg: { aspect: 1.25, window: [[0.05, 0.0625], [0.93, 0.0625], [0.83, 0.9375], [0.05, 0.9375]] },
  },
});

/**
 * **今使う装飾の形**（画像が入っていれば画像の、無ければ簡略版の縦横比・窓）。
 * HUD は箱の大きさと中身の切り抜きをこれで決める（差し替えた画像ごとに窓の位置が違うため）。
 */
export function ornamentSpec(name) {
  const o = ORNAMENTS[name];
  if (!o) return null;
  const img = o.image && (typeof o.image === 'string' ? { src: o.image } : o.image);
  const src = img ? (typeof img.src === 'string' ? img.src : (img.src?.default ?? null)) : null;
  const shape = src ? { ...o.svg, ...img } : o.svg;
  return { src, aspect: shape.aspect ?? (o.size.w / o.size.h), window: shape.window ?? null, number: shape.number ?? null };
}

/** 窓の多角形 → CSS の `clip-path`（`flip` で左右反転） */
export function windowClip(win, flip = false) {
  if (!win) return undefined;
  const pts = win.map(([x, y]) => `${((flip ? 1 - x : x) * 100).toFixed(2)}% ${(y * 100).toFixed(2)}%`);
  return `polygon(${pts.join(', ')})`;
}

/**
 * **装飾を 1 つ描く**。画像が入っていれば画像、無ければ簡略版の SVG。
 * `flip` で左右反転（P2 の側）。箱の大きさは親が決める（`position: absolute; inset: 0` で広がる）。
 */
export function Ornament({ name, flip = false, className = '' }) {
  const o = ORNAMENTS[name];
  if (!o) return null;
  const style = flip ? { transform: 'scaleX(-1)' } : undefined;
  const { src } = ornamentSpec(name);
  return (
    <div className={`cx-orn cx-orn--${name} ${className}`} style={style} data-ornament={name} data-ornament-src={src ? 'image' : 'svg'}>
      {src ? <img src={src} alt="" draggable={false} /> : <o.Svg />}
    </div>
  );
}
